import React, { createContext, useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signupSuccessDialog, setSignupSuccessDialog] = useState(false);

  const fetchUserProfile = useCallback(async (userId) => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (profileError && profileError.code !== 'PGRST116') throw profileError;
      return profileData || null;
    } catch (error) {
      console.error("AuthContext: Error fetching profile.", error);
      return null;
    }
  }, []);

  useEffect(() => {
    let subscription = null;

    const initializeAuth = async () => {
      try {
        // 1. 현재 세션 확인
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error("AuthContext: Error getting session.", sessionError);
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }

        // 2. 세션이 있으면 사용자 설정
        if (session?.user) {
          setUser(session.user);
          const profile = await fetchUserProfile(session.user.id);
          setUserProfile(profile);
        } else {
          setUser(null);
          setUserProfile(null);
        }

        setLoading(false);

        // 3. 인증 상태 변경 리스너 설정
        const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
          async (event, session) => {
            console.log('Auth state changed:', event, session?.user?.id);

            // 세션 상태에 따라 사용자 정보 업데이트
            if (session?.user) {
              setUser(session.user);
              const profile = await fetchUserProfile(session.user.id);
              setUserProfile(profile);
            } else {
              setUser(null);
              setUserProfile(null);
            }

            // 토큰 갱신 시에는 로딩 상태를 변경하지 않음
            if (event !== 'TOKEN_REFRESHED') {
              setLoading(false);
            }
          }
        );

        subscription = authSubscription;
      } catch (error) {
        console.error("AuthContext: Error initializing auth.", error);
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
    };

    initializeAuth();

    // 컴포넌트 언마운트 시 구독 해제
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [fetchUserProfile]);
  
  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        console.error('Login Error:', error.message);
        setLoading(false);
        throw error;
      }
      // onAuthStateChange가 나머지를 처리하므로 여기서는 로딩만 해제
      return data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Logout Error:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const signup = useCallback(async (formData) => {
    try {
      console.log('AuthContext: signup 함수 호출됨', formData);
      
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.name,
            email: formData.email
          }
        }
      });

      if (error) {
        console.error('Signup Error:', error);
        return { success: false, error: error.message };
      }

      console.log('Signup Success:', data);
      setSignupSuccessDialog(true);
      return { success: true, data };
    } catch (error) {
      console.error('Signup Exception:', error);
      return { success: false, error: error.message };
    }
  }, []);
  
  const updateProfile = useCallback(async (profileData) => {
      if (!user) throw new Error("사용자가 로그인되어 있지 않습니다.");

      const { data, error } = await supabase
          .from('profiles')
          .update(profileData)
          .eq('id', user.id)
          .select()
          .single();

      if (error) throw error;
      
      setUserProfile(prev => ({...prev, ...data}));
      return data;
  }, [user]);

  // 세션 갱신 함수 추가
  const refreshSession = useCallback(async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession();
      if (error) {
        console.error('Session refresh error:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Session refresh exception:', error);
      return false;
    }
  }, []);

  // 토큰 상태 체크 및 자동 갱신 함수 추가
  const checkAndRefreshToken = useCallback(async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        return false;
      }

      // 토큰 만료 5분 전에 갱신
      const now = Math.floor(Date.now() / 1000);
      const expiresAt = session.expires_at;
      const refreshThreshold = 5 * 60; // 5분

      if (expiresAt && (expiresAt - now) < refreshThreshold) {
        console.log('토큰 만료 임박, AuthContext에서 자동 갱신...');
        return await refreshSession();
      }

      return true;
    } catch (error) {
      console.error('토큰 체크 중 오류:', error);
      return false;
    }
  }, [refreshSession]);

  // 주기적 토큰 체크 (5분마다)
  useEffect(() => {
    if (!user) return;

    const tokenCheckInterval = setInterval(async () => {
      const isValid = await checkAndRefreshToken();
      if (!isValid) {
        console.log('토큰 갱신 실패, 로그아웃 처리');
        // 토큰 갱신 실패 시 로그아웃 처리는 하지 않고 다음 API 호출에서 처리하도록 함
      }
    }, 5 * 60 * 1000); // 5분마다

    return () => clearInterval(tokenCheckInterval);
  }, [user, checkAndRefreshToken]);

  const value = useMemo(() => ({
    user,
    userProfile,
    loading,
    isLoggedIn: !!user,
    login,
    logout,
    signup,
    updateProfile,
    refreshSession,
    checkAndRefreshToken,
    signupSuccessDialog,
    setSignupSuccessDialog,
    isAdmin: userProfile?.role === 'admin',
    isEvaluator: userProfile?.role === 'admin' || (userProfile?.permissions?.can_evaluate_mee || userProfile?.permissions?.can_evaluate_mpt),
  }), [user, userProfile, loading, login, logout, signup, updateProfile, refreshSession, checkAndRefreshToken, signupSuccessDialog]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  return useContext(AuthContext);
}; 